import { Fragment } from 'react'

import type { Audience, Budget, Market, Objective } from '../../demo/types'
import { Button, Card, GlossaryText, Icon, Spinner, useGlossaryTerm } from '../../ui'
import { AudiencePicker } from './AudiencePicker'
import { BudgetField } from './BudgetField'
import common from './CreatePage.module.css'
import { createTakeoverCopy } from './copy'
import { MarketPicker } from './MarketPicker'
import { ObjectivePicker } from './ObjectivePicker'
import { displayPlanDraftField, PLAN_DRAFT, type PlanDraftFieldKey, type PlanDraftState } from './planDraftData'
import styles from './PlanDraft.module.css'

interface PlanDraftProps {
  draft: PlanDraftState
  touched: ReadonlySet<PlanDraftFieldKey>
  openEditor: PlanDraftFieldKey | 'all' | null
  busy: boolean
  onToggle: (key: PlanDraftFieldKey | 'all') => void
  onChange: (key: PlanDraftFieldKey, value: Objective | Market[] | Audience | Budget) => void
  onConfirm: () => void
}

const roleKey: Record<PlanDraftFieldKey, string> = {
  objective: 'objective',
  markets: 'market',
  audience: 'audience',
  budget: 'budget',
}

export function PlanDraft({ draft, touched, openEditor, busy, onToggle, onChange, onConfirm }: PlanDraftProps) {
  const copy = createTakeoverCopy.planCard
  const cpa = useGlossaryTerm('cpa')
  const roas = useGlossaryTerm('roas')
  const editor = (key: PlanDraftFieldKey) => {
    if (key === 'objective') return <ObjectivePicker value={draft.objective} onChange={(value) => onChange(key, value)} />
    if (key === 'markets') return <MarketPicker value={draft.markets} onChange={(value) => onChange(key, value)} />
    if (key === 'audience') return <AudiencePicker value={draft.audience} onChange={(value) => onChange(key, value)} />
    return <BudgetField value={draft.budget} onChange={(value) => onChange(key, value)} />
  }
  return (
    <Card className={common.takeoverCard} data-scope="plan-draft">
      <div className={common.cardHead}>
        <span className={common.aiBadge}><Icon name="sparkle" size={12} />{copy.badge}</span>
        <h3>{copy.title}</h3>
        <span className={common.cardStep}>{copy.step}</span>
      </div>
      <div className={`${common.cardBody} ${styles.body}`}>
        {PLAN_DRAFT.fields.map((field) => {
          const key = field.key
          const display = touched.has(key) ? displayPlanDraftField(key, draft) : field.display
          const expanded = openEditor === 'all' || openEditor === key
          const basis = touched.has(key) ? `你已把${field.label}改为「${display}」，本项不再由 AI 决定。` : field.basis
          return (
            <Fragment key={key}>
              <div className={styles.row} data-role={`row-${roleKey[key]}`}>
                <div className={styles.label}>{field.label}</div>
                <div className={styles.value}><div>{display}</div></div>
                <div className={`${common.tiny} ${common.muted} ${styles.basis}`}><GlossaryText text={basis} /></div>
                <Button variant="ghost" className={styles.editButton} data-role={`edit-${roleKey[key]}`} aria-expanded={expanded} disabled={busy} onClick={() => onToggle(key)}>{copy.edit}</Button>
              </div>
              {expanded ? <div className={styles.editor} data-context="plan-editor">{editor(key)}</div> : null}
            </Fragment>
          )
        })}
        <div className={styles.actions}>
          <Button className={styles.confirmButton} data-role="confirm" disabled={busy} onClick={onConfirm}>{busy ? <Spinner /> : null}{copy.confirm}<Icon name="chevron-right" size={17} /></Button>
          <Button variant="ghost" className={styles.editAllButton} data-role="edit-all" aria-expanded={openEditor === 'all'} disabled={busy} onClick={() => onToggle('all')}>{copy.editAll}</Button>
          <span className={`${common.tiny} ${common.muted} ${styles.footnote}`}>{copy.footnote}</span>
        </div>
        <div className={common.klist} data-role="metric-glossary">
          {[cpa, roas].map((term) => term ? <span key={term.key}><b>{term.label}</b> · {term.hover}</span> : null)}
        </div>
      </div>
    </Card>
  )
}
